import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Images, Play } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import Navbar from "../components/Navbar";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const KIND_TABS = [
  { kind: "photo", label: "Photos" },
  { kind: "chat", label: "Chats" },
  { kind: "video", label: "Videos" },
];

const fmtDuration = (secs) => {
  if (!secs || !isFinite(secs)) return null;
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

function VideoThumb({ item, onOpen }) {
  const [dur, setDur] = useState(null);
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="group relative mb-5 block w-full break-inside-avoid overflow-hidden rounded-2xl border border-line bg-panel transition-all duration-300 hover:border-accent/60 hover:shadow-glow-emerald"
      data-testid={`gallery-item-${item.id}`}
    >
      <video
        src={`${API_BASE}${item.url}`}
        preload="metadata"
        muted
        playsInline
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        className="w-full transition-transform duration-500 group-hover:scale-[1.03]"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-ink/35 transition-colors duration-300 group-hover:bg-ink/20">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-ink shadow-glow-emerald transition-transform duration-300 group-hover:scale-110">
          <Play className="ml-0.5 h-6 w-6" />
        </span>
      </span>
      {dur != null && (
        <span className="absolute bottom-3 right-3 rounded-full bg-ink/85 border border-line px-2.5 py-1 font-mono text-[11px] font-bold text-white backdrop-blur-sm" data-testid={`gallery-video-duration-${item.id}`}>
          {fmtDuration(dur)}
        </span>
      )}
      <span className="absolute top-3 left-3 rounded-full bg-gold/90 px-2.5 py-0.5 font-mono text-[9px] font-extrabold uppercase tracking-[0.2em] text-ink">
        Video
      </span>
    </button>
  );
}

function PhotoThumb({ item, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="group relative mb-5 block w-full break-inside-avoid overflow-hidden rounded-2xl border border-line bg-panel transition-all duration-300 hover:border-accent/60 hover:shadow-glow-emerald"
      data-testid={`gallery-item-${item.id}`}
    >
      <img
        src={`${API_BASE}${item.url}`}
        alt="Client transformation result"
        className="w-full transition-transform duration-500 group-hover:scale-[1.03]"
        loading="lazy"
      />
      <span className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
    </button>
  );
}

export default function Gallery() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api("/gallery")
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  const kindsPresent = items ? [...new Set(items.map((i) => i.kind))] : [];
  const tabs = ["All", ...KIND_TABS.filter((k) => kindsPresent.includes(k.kind)).map((k) => k.label)];
  const filtered = !items || filter === "All" ? items : items.filter((i) => filter === "Photos" ? i.kind === "photo" : filter === "Chats" ? i.kind === "chat" : i.kind === "video");

  const close = useCallback(() => setLightbox(null), []);
  const step = useCallback(
    (dir) => {
      setLightbox((cur) => {
        if (cur === null || !filtered) return cur;
        return (cur + dir + filtered.length) % filtered.length;
      });
    },
    [filtered]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, step]);

  const current = lightbox !== null && filtered ? filtered[lightbox] : null;

  return (
    <div className="min-h-screen bg-ink text-white overflow-x-clip">
      <Seo
        title="Client Transformation Gallery | Fitness Fanatic Nik"
        description="Real client transformation photos and workout videos from coach Nikhil Shrirao — fat loss, toning and recomposition wins."
      />
      <div className="grain" aria-hidden="true" />
      <Navbar crossPage />
      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-40 pb-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-400 mb-4 flex items-center gap-3">
            <span className="h-px w-8 bg-emerald-400/60" />
            The Gallery
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-white">
            Proof In Pictures
          </h1>
          <p className="mt-4 text-slate-400 leading-relaxed">
            Real clients, real transformations — tap any photo to enlarge, or any video to watch it right here.
          </p>
        </motion.div>

        {items && items.length > 0 && tabs.length > 2 && (
          <div className="mt-10 flex flex-wrap gap-2.5" data-testid="gallery-tabs">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                data-testid={`gallery-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wide border transition-all duration-300 ${
                  filter === t
                    ? "bg-accent text-ink border-accent shadow-glow-emerald"
                    : "bg-panel text-slate-300 border-line hover:border-accent/50"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {items === null ? (
          <div className="flex justify-center py-32">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 rounded-3xl border border-dashed border-line bg-panel p-16 text-center" data-testid="gallery-empty-state">
            <Images className="h-10 w-10 text-slate-600 mx-auto" />
            <p className="mt-5 font-display text-xl font-bold uppercase text-white">Gallery coming soon</p>
            <p className="mt-2 text-sm text-slate-400">Client results are being curated — check back shortly.</p>
          </div>
        ) : (
          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-5 [column-fill:_balance]">
            {filtered.map((item, i) =>
              item.kind === "video" ? (
                <VideoThumb key={item.id} item={item} onOpen={() => setLightbox(i)} />
              ) : (
                <PhotoThumb key={item.id} item={item} onOpen={() => setLightbox(i)} />
              )
            )}
          </div>
        )}
      </main>

      <AnimatePresence>
        {current && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 backdrop-blur-xl p-4"
            data-testid="gallery-lightbox"
            onClick={close}
          >
            <button
              type="button"
              className="absolute top-6 right-6 z-10 p-3 rounded-full border border-line text-slate-300 transition-colors hover:text-white hover:border-slate-500"
              onClick={close}
              aria-label="Close"
              data-testid="gallery-lightbox-close"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              type="button"
              className="absolute left-4 lg:left-10 z-10 p-3 rounded-full border border-line text-slate-300 transition-colors hover:text-white hover:border-accent/60"
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
              aria-label="Previous"
              data-testid="gallery-lightbox-prev"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            {current.kind === "video" ? (
              <motion.video
                key={current.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                src={`${API_BASE}${current.url}`}
                controls
                autoPlay
                playsInline
                className="max-h-[86vh] max-w-full rounded-2xl border border-line"
                onClick={(e) => e.stopPropagation()}
                data-testid="gallery-lightbox-video"
              />
            ) : (
              <motion.img
                key={current.id}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25 }}
                src={`${API_BASE}${current.url}`}
                alt="Client transformation result — enlarged"
                className="max-h-[86vh] max-w-full object-contain rounded-2xl border border-line"
                onClick={(e) => e.stopPropagation()}
                data-testid="gallery-lightbox-image"
              />
            )}
            <button
              type="button"
              className="absolute right-4 lg:right-10 z-10 p-3 rounded-full border border-line text-slate-300 transition-colors hover:text-white hover:border-accent/60"
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
              aria-label="Next"
              data-testid="gallery-lightbox-next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
            <span className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-xs text-slate-500">
              {lightbox + 1} / {filtered.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {current === null && (
        <div className="pb-16 text-center">
          <Link to="/" className="text-sm text-slate-500 hover:text-emerald-400 transition-colors" data-testid="gallery-back-home">
            ← Back to home
          </Link>
        </div>
      )}
    </div>
  );
}